import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { BookOpen, ChevronDown, Download, File, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { SpecSheetRow } from "./SpecSheetRow";
import { downloadAssetDocument } from "./useAssetDocuments";
import type { AssetDocument } from "./types";
import type { SpecSheetRef } from "@/features/assets/types/lookupAssetSpecs";

function getDocumentIcon(docType: string, mimeType: string | null) {
  const type = docType?.toLowerCase() || '';
  const mime = mimeType?.toLowerCase() || '';

  if (type.includes('manual') || mime.includes('pdf')) return BookOpen;
  if (mime.includes('image')) return ImageIcon;
  return File;
}

interface DocumentsCardProps {
  documents: AssetDocument[];
  loadingDocuments: boolean;
  specSheetCount: number;
  loadingSpecSheets: boolean;
  specLookupStatus: string;
  specSkipReason: string | null | undefined;
  specLookupMessage: string | null | undefined;
  primaryPdf: SpecSheetRef | null | undefined;
  visibleRelatedPdfs: SpecSheetRef[];
}

export function DocumentsCard({
  documents,
  loadingDocuments,
  specSheetCount,
  loadingSpecSheets,
  specLookupStatus,
  specSkipReason,
  specLookupMessage,
  primaryPdf,
  visibleRelatedPdfs,
}: DocumentsCardProps) {
  const handleSpecSheetOpen = (fileUrl: string) => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="animate-fade-in border-muted">
      <Collapsible>
        <CardHeader className="pb-4">
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Spec sheets &amp; documents
                <Badge variant="secondary" className="ml-2">
                  {specSheetCount + documents.length}
                </Badge>
              </CardTitle>
              <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200" />
            </div>
            <p className="text-sm text-muted-foreground mt-1 text-left font-normal">
              Manufacturer spec PDFs and uploaded files
            </p>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Manufacturer spec sheets
              </p>
              {loadingSpecSheets ? (
                <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <p className="text-sm">Loading spec sheets...</p>
                </div>
              ) : specLookupStatus === 'skipped' ? (
                <p className="text-sm text-muted-foreground py-2">{specSkipReason}</p>
              ) : specLookupStatus === 'error' ? (
                <p className="text-sm text-destructive py-2">
                  Could not load spec sheets. Try again later.
                </p>
              ) : specSheetCount > 0 ? (
                <div className="space-y-3">
                  {/* Hide the "no decoder rule indexed" message in the related_only
                      case — absence of a Primary spec row already conveys it, and the
                      related list is capped to MAX_RELATED_SPEC_SHEETS below. */}
                  {specLookupMessage && primaryPdf?.file_url && (
                    <p className="text-xs text-muted-foreground">{specLookupMessage}</p>
                  )}
                  {primaryPdf?.file_url && (
                    <SpecSheetRow
                      sheet={primaryPdf}
                      badgeLabel="Primary spec"
                      onOpen={handleSpecSheetOpen}
                    />
                  )}
                  {visibleRelatedPdfs.map((sheet) => (
                    <SpecSheetRow
                      key={sheet.id}
                      sheet={sheet}
                      badgeLabel="Related"
                      onOpen={handleSpecSheetOpen}
                    />
                  ))}
                </div>
              ) : specLookupStatus === 'not_found' ? (
                <p className="text-sm text-muted-foreground py-2">
                  No manufacturer spec sheet found for this model.
                </p>
              ) : null}
            </div>

            {loadingDocuments && documents.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Loading uploaded files...</p>
            ) : documents.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Uploaded files
                </p>
                {documents.map((doc, idx) => {
                  const DocIcon = getDocumentIcon(doc.document_type, doc.mime_type);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 rounded-lg border-2 border-border hover:border-primary transition-all duration-300 group animate-fade-in"
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                          <DocIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{doc.document_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {doc.document_type.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2 hover-scale"
                        onClick={() => downloadAssetDocument(doc)}
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
